/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface IRoleData {
	data: any;
	roleId: string;
	roleName: string;
}

/**
 * This component is only designed to be used in the "Add New" part of "role-schedules.html"
 */
@Component({
	selector: 'mat-role-select',
	templateUrl: './mat-role-select.component.html',
	styleUrls: ['./mat-role-select.component.scss'],
})
export class MatRoleSelectComponent implements ControlValueAccessor, OnInit, OnDestroy {
	@Input() public options: { $id: string; data: { roleName: string } }[];
	@Input() public initialValue: unknown[];
	@Input() public multiple = false;
	@Output() public selection: EventEmitter<any> = new EventEmitter();

	public option$: Observable<Map<string, IRoleData>>;
	public internalFormControl: UntypedFormControl;

	// Field to support ControlValueAccessor functionality
	private onChange: any = () => {};

	private shutdownSubject$: Subject<boolean> = new Subject();

	public ngOnInit(): void {
		// Convert incoming array to a Map:
		const roleMap = new Map<string, IRoleData>();
		for (const elem of this.options) {
			roleMap.set(elem.$id, {
				data: elem,
				roleId: elem.$id,
				roleName: elem.data.roleName,
			});
		}
		this.option$ = of(roleMap);
		this.internalFormControl = new UntypedFormControl();

		//only support initialValue with multiple for now
		if (this.multiple && this.initialValue && this.initialValue.length > 0) {
			this.internalFormControl.setValue(this.initialValue, { emitEvent: false });
		}

		this.internalFormControl.valueChanges.pipe(takeUntil(this.shutdownSubject$)).subscribe((newValue) => {
			this.selection.emit(newValue);
		});
	}

	public writeValue(obj: any): void {
		this.internalFormControl.setValue(obj, { emitEvent: false });
	}

	public registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: any): void {
		// We don't particularly care about the given "fn"
	}

	public setDisabledState?(isDisabled: boolean): void {
		if (isDisabled) {
			this.internalFormControl.disable();
		} else {
			this.internalFormControl.enable();
		}
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.unsubscribe();
	}
}
